// Components/Pages/BlogList.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FaSearch, 
  FaCalendarAlt, 
  FaUser, 
  FaComments,
  FaArrowRight,
  FaSpinner,
  FaClock,
  FaTag
} from "react-icons/fa";

const API_BASE = 'https://api.rigokenya.org';

function BlogList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/blog`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch blog posts');
      }

      const data = await response.json();
      
      const processedData = data.map(post => ({
        ...post,
        read_time: post.read_time || post.readTime || '3 min read',
        excerpt: post.excerpt || post.summary || '',
        image: post.image ? (
          post.image.startsWith('/uploads/') ? `${API_BASE}${post.image}` : post.image
        ) : null,
        tags: Array.isArray(post.tags) ? post.tags : (post.tags ? post.tags.split(',') : [])
      }));
      
      setBlogPosts(processedData);
      
      const uniqueCategories = ["All", ...new Set(processedData.map(post => post.category).filter(Boolean))];
      setCategories(uniqueCategories);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setBlogPosts([]);
      setCategories(["All"]);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/api/placeholder/800/500';
    if (imagePath.startsWith('/uploads/')) {
      return `${API_BASE}${imagePath}`;
    }
    return imagePath;
  };

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          post.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPost = blogPosts.find(post => post.featured);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-5xl text-primaryGold mx-auto mb-4" />
          <p className="text-gray-600">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-primaryBlue text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="text-primaryGold">Blog</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8">
              Stories, updates, and insights from our community initiatives
            </p>
            
            <div className="relative max-w-2xl mx-auto">
              <input
                type="text"
                placeholder="Search articles, topics, or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-6 py-4 pl-14 rounded-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-primaryGold shadow-lg"
              />
              <FaSearch className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? "bg-primaryBlue text-white shadow-md"
                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
          <span className="text-sm text-gray-500">
            {filteredPosts.length} {filteredPosts.length === 1 ? "post" : "posts"}
          </span>
        </div>

        {/* Featured Post */}
        {featuredPost && !searchTerm && selectedCategory === "All" && (
          <div className="mb-12 bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="h-64 md:h-auto">
                <img 
                  src={getImageUrl(featuredPost.image)}
                  alt={featuredPost.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/api/placeholder/800/500';
                  }}
                />
              </div>
              <div className="p-6 md:p-8 flex flex-col justify-center">
                <span className="text-primaryGold font-semibold text-sm uppercase tracking-wider">
                  Featured Post
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mt-2 mb-3">
                  {featuredPost.title}
                </h2>
                <p className="text-gray-600 mb-4">{featuredPost.excerpt}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
                  <span className="flex items-center gap-1">
                    <FaUser /> {featuredPost.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <FaCalendarAlt /> {featuredPost.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <FaComments /> {featuredPost.comments || 0} comments
                  </span>
                </div>
                <Link 
                  to={`/blog/${featuredPost.id}`}
                  className="inline-flex items-center gap-2 text-primaryBlue font-semibold hover:text-primaryGold transition-colors duration-300 group"
                >
                  Read Full Story 
                  <FaArrowRight className="group-hover:translate-x-2 transition-transform duration-300" />
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Blog Grid */}
        {filteredPosts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filteredPosts.map((post) => (
              <article 
                key={post.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <Link to={`/blog/${post.id}`}>
                  <img 
                    src={getImageUrl(post.image)}
                    alt={post.title}
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/800/500';
                    }}
                  />
                </Link>
                <div className="p-6">
                  <span className="inline-block px-3 py-1 bg-primaryBlue/10 text-primaryBlue text-xs font-semibold rounded-full mb-3">
                    {post.category}
                  </span>
                  <Link to={`/blog/${post.id}`}>
                    <h3 className="text-xl font-bold text-gray-800 hover:text-primaryBlue transition-colors duration-300 mb-2 line-clamp-2">
                      {post.title}
                    </h3>
                  </Link>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags?.slice(0, 3).map((tag, index) => (
                      <span 
                        key={index}
                        className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <FaUser className="text-primaryGold" />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <FaCalendarAlt className="text-xs" />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <FaComments className="text-xs" />
                        {post.comments || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <h3 className="text-2xl font-semibold text-gray-700 mb-2">No posts found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </section>
    </div>
  );
}

export default BlogList;